import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { BadgeCheck } from "lucide-react";
import { fileUrl, COUNTRY_NAME, regLabel } from "@/lib/api";
import { ToothLogo } from "@/components/ToothLogo";

export default function ProCard({ pro, index = 0 }) {
  const navigate = useNavigate();
  const verified = pro.plan === "verified";
  const photo = pro.photos?.[0];
  const nameLen = (pro.name || "").length;
  const nameSize = nameLen > 26 ? 14 : nameLen > 22 ? 16 : nameLen > 18 ? 18 : 20;
  const cityLine = `${pro.city}. ${COUNTRY_NAME[pro.country] || ""}`;
  const specialty0 = pro.specialties?.[0] || "";
  const twoLine = nameLen > 22 || cityLine.length + specialty0.length > 26;

  return (
    <motion.button
      data-testid={`pro-card-${pro.id}`}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.06, 0.4) }}
      whileTap={{ scale: 0.985 }}
      onClick={() => navigate(`/perfil/${pro.id}`)}
      className="flex w-full overflow-hidden rounded-[28px] bg-white text-left"
    >
      <div className="relative w-[40%] shrink-0 bg-neutral-200">
        {photo ? (
          <img src={fileUrl(photo)} alt={pro.name} loading="lazy" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full min-h-[168px] w-full items-center justify-center">
            <img
              src="/assets/tooth-placeholder.png"
              alt=""
              className="h-9 w-9 object-contain opacity-25"
            />
          </div>
        )}
      </div>

      <div className="flex min-h-[172px] flex-1 flex-col justify-between p-4">
        <div>
          <h3 className="font-brand font-normal leading-tight text-neutral-900" style={{ fontSize: nameSize }}>
            {pro.name}
            {verified && (
              <BadgeCheck className="ml-1 inline-block h-5 w-5 -translate-y-0.5 fill-neutral-900 align-middle text-white" />
            )}
          </h3>
          <p className="mt-0.5 truncate text-xs text-neutral-400">{regLabel(pro.country)} {pro.senacyt || "—"}</p>

          {verified && pro.tags?.length > 0 && (
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {pro.tags.slice(0, 3).map((t) => (
                <span key={t} className="rounded-full px-2.5 py-0.5 text-xs text-neutral-700" style={{ backgroundColor: "#D9D9D9" }}>
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>

        {twoLine ? (
          <div className="text-xs text-neutral-700">
            <p className="font-medium">{cityLine}</p>
            <p className="truncate">{specialty0}</p>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-xs text-neutral-700">
            <span className="whitespace-nowrap font-medium">
              {cityLine}
            </span>
            <span className="text-neutral-300">|</span>
            <span className="truncate">{specialty0}</span>
          </div>
        )}
      </div>
    </motion.button>
  );
}
